import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Bell, Send, X } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

export default function NotificationPanel({ relatedToType, relatedToId, relatedToName }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const { data: currentUser } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const handleUserToggle = (userEmail) => {
    setSelectedUsers(prev => 
      prev.includes(userEmail) 
        ? prev.filter(e => e !== userEmail)
        : [...prev, userEmail]
    );
  };

  const handleSendNotification = async () => {
    if (!message.trim() || selectedUsers.length === 0) {
      toast.error("Vennligst skriv en melding og velg minst én mottaker");
      return;
    }

    setIsSending(true);
    try {
      const subject = `Varsel: ${relatedToName || "Ny melding"}`;
      const body = `
${currentUser?.full_name || "En bruker"} har sendt deg en melding:

${message}

---
Dette varselet er relatert til: ${relatedToName}
Type: ${relatedToType === "project" ? "Prosjekt" : relatedToType === "task" ? "Oppgave" : "Team"}
      `;

      // Send email to each selected user
      const promises = selectedUsers.map(userEmail =>
        base44.integrations.Core.SendEmail({
          to: userEmail,
          subject: subject,
          body: body,
          from_name: currentUser?.full_name,
        })
      );

      await Promise.all(promises);
      
      toast.success(`Varsel sendt til ${selectedUsers.length} bruker(e)`);
      setMessage("");
      setSelectedUsers([]);
      setOpen(false);
    } catch (error) {
      toast.error("Kunne ikke sende varsel");
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  const availableUsers = users.filter(u => u.email !== currentUser?.email);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Bell className="w-4 h-4 mr-2" />
          Send Varsel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Varsel til Teammedlemmer</DialogTitle>
          <DialogDescription>
            Send en melding til utvalgte brukere relatert til {relatedToName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="message">Melding *</Label>
            <Textarea
              id="message"
              placeholder="Skriv din melding her..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label>Velg Mottakere *</Label>
            <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
              {availableUsers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-2">Ingen andre brukere tilgjengelig</p>
              ) : (
                availableUsers.map((user) => (
                  <div key={user.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.email)}
                      onCheckedChange={() => handleUserToggle(user.email)}
                    />
                    <Label
                      htmlFor={`user-${user.id}`}
                      className="text-sm font-normal cursor-pointer flex-1"
                    >
                      {user.full_name} ({user.email})
                    </Label>
                  </div>
                ))
              )}
            </div>
            {selectedUsers.length > 0 && (
              <p className="text-xs text-slate-500">{selectedUsers.length} bruker(e) valgt</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleSendNotification}
              disabled={isSending || !message.trim() || selectedUsers.length === 0}
              className="bg-zinc-900 hover:bg-zinc-900"
            >
              <Send className="w-4 h-4 mr-2" />
              {isSending ? "Sender..." : "Send Varsel"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}